import { collection, doc, getDoc, getDocs, getFirestore, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { esTransicionValida, SIRE_ESTADOS } from "./sireEstados";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const buildPeriodoDocId = ({ rucEmpresa, periodo, tipo }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();
  if (!cleanRuc || !cleanPer || !cleanTip) {
    throw new Error("RUC, Periodo y Tipo son obligatorios para identificar el ciclo SIRE.");
  }
  return `${cleanRuc}_${cleanPer}_${cleanTip}`;
};

export const obtenerEstadoSirePeriodo = async ({ rucEmpresa, periodo, tipo }) => {
  const docId = buildPeriodoDocId({ rucEmpresa, periodo, tipo });
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_PERIODOS, docId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    return {
      docId,
      ruc_empresa: cleanText(rucEmpresa),
      periodo: cleanText(periodo),
      tipo: cleanText(tipo).toUpperCase(),
      estado: SIRE_ESTADOS.NO_INICIADO,
      propuesta_sunat_id: docId,
      propuesta_origen: "MOCK_SUNAT_SIRE",
      total_sunat: 0,
      total_local: 0,
      total_conciliados: 0,
      total_diferencias: 0,
      archivo_generado: false,
      archivo_enviado: false,
      archivo_aceptado: false,
      existe: false,
    };
  }

  return { docId: snap.id, existe: true, ...snap.data() };
};

export const actualizarEstadoSirePeriodo = async ({ rucEmpresa, periodo, tipo, nuevoEstado, metadata = {} }) => {
  if (!Object.values(SIRE_ESTADOS).includes(nuevoEstado)) {
    throw new Error(`Estado '${nuevoEstado}' no pertenece a la enumeración oficial SIRE_ESTADOS.`);
  }

  const docId = buildPeriodoDocId({ rucEmpresa, periodo, tipo });
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_PERIODOS, docId);
  const actualObj = await obtenerEstadoSirePeriodo({ rucEmpresa, periodo, tipo });
  const estadoActual = actualObj.estado;

  const totalDiferencias = metadata.total_diferencias !== undefined ? toNum(metadata.total_diferencias) : toNum(actualObj.total_diferencias);

  // REGLA 1: No se puede marcar CONCILIADO si total_diferencias > 0
  if (nuevoEstado === SIRE_ESTADOS.CONCILIADO && totalDiferencias > 0) {
    throw new Error("No se puede marcar el periodo como CONCILIADO cuando existen diferencias sin resolver.");
  }

  // REGLA 2: No se puede pasar a LISTO_PARA_GENERAR si no estaba CONCILIADO
  if (nuevoEstado === SIRE_ESTADOS.LISTO_PARA_GENERAR && estadoActual !== SIRE_ESTADOS.CONCILIADO) {
    throw new Error("No se puede pasar a LISTO_PARA_GENERAR si el periodo no está en estado CONCILIADO.");
  }

  // Validar transiciones permitidas
  if (!esTransicionValida(estadoActual, nuevoEstado)) {
    throw new Error(`Transición no permitida de '${estadoActual}' a '${nuevoEstado}'.`);
  }

  const payload = {
    ruc_empresa: cleanText(rucEmpresa),
    periodo: cleanText(periodo),
    tipo: cleanText(tipo).toUpperCase(),
    estado: nuevoEstado,
    propuesta_sunat_id: docId,
    propuesta_origen: cleanText(metadata.propuesta_origen || actualObj.propuesta_origen || "MOCK_SUNAT_SIRE"),
    total_sunat: metadata.total_sunat !== undefined ? toNum(metadata.total_sunat) : toNum(actualObj.total_sunat),
    total_local: metadata.total_local !== undefined ? toNum(metadata.total_local) : toNum(actualObj.total_local),
    total_conciliados: metadata.total_conciliados !== undefined ? toNum(metadata.total_conciliados) : toNum(actualObj.total_conciliados),
    total_diferencias: totalDiferencias,
    archivo_generado: metadata.archivo_generado !== undefined ? Boolean(metadata.archivo_generado) : Boolean(actualObj.archivo_generado),
    archivo_enviado: metadata.archivo_enviado !== undefined ? Boolean(metadata.archivo_enviado) : Boolean(actualObj.archivo_enviado),
    archivo_aceptado: metadata.archivo_aceptado !== undefined ? Boolean(metadata.archivo_aceptado) : Boolean(actualObj.archivo_aceptado),
    updated_at: serverTimestamp(),
  };

  if (!actualObj.existe) {
    payload.created_at = serverTimestamp();
  }

  await setDoc(docRef, payload, { merge: true });
  return { docId, estado: nuevoEstado, ...payload };
};

export const puedeGenerarArchivoSire = ({ estado, totalDiferencias, total_diferencias }) => {
  const dif = totalDiferencias !== undefined ? toNum(totalDiferencias) : toNum(total_diferencias);
  const estadosPermitidos = [SIRE_ESTADOS.CONCILIADO, SIRE_ESTADOS.LISTO_PARA_GENERAR, SIRE_ESTADOS.ARCHIVO_GENERADO];
  return estadosPermitidos.includes(estado) && dif === 0;
};

export const marcarListoParaGenerar = async ({ rucEmpresa, periodo, tipo }) => {
  return actualizarEstadoSirePeriodo({
    rucEmpresa,
    periodo,
    tipo,
    nuevoEstado: SIRE_ESTADOS.LISTO_PARA_GENERAR,
  });
};
